<template>
  <!-- Informações Básicas -->
  <v-form @submit.prevent ref="form">
    <TopicTitle
      class="mb-4"
      :title="$t('new-event.basic-information.title')"
      icon="mdi-text"
    />
    <v-row no-gutters>
      <v-col sm="4" cols="12">
        <BaseTextField
          :placeholder="$t('new-event.basic-information.placeholders.name')"
          required
          class="mb-4"
          :label="`${$t('new-event.basic-information.placeholders.name')} ${
            selectedLocales.length > 0 ? `(${getTextLocale(locale)})` : ''
          }`"
          :rules="[rules.required]"
          v-model="nameDefault"
          maxlength="65"
        />
        <div v-for="locale in selectedLocales" :key="locale">
          <v-text-field
            required
            class="mb-4 rounded-lg"
            :placeholder="$t('new-event.basic-information.placeholders.name')"
            :label="`${$t(
              'new-event.basic-information.placeholders.name'
            )} (${getTextLocale(locale)})`"
            :rules="[rules.required]"
            v-model="nameLocales[locale]"
            hide-details="auto"
            dense
            outlined
            persistent-placeholder
            maxlength="65"
          >
            <template v-slot:append-outer>
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-icon @click="clearNameLocale(locale)"
                      >mdi-trash-can-outline</v-icon
                    >
                  </div>
                </template>
                <span>{{
                  $t("new-event.basic-information.remove-locale")
                }}</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </div>
        <BaseTextField
          required
          class="mb-4"
          :label="$t('new-event.basic-information.inputs.url-link')"
          :placeholder="$t('new-event.basic-information.placeholders.url-link')"
          :rules="[rules.required, rules.validURL]"
          v-model="getEventBasicData.urlLink"
          v-if="getEventType === 'LINK'"
          maxlength="250"
        />
        <v-autocomplete
          :label="$t('new-event.basic-information.inputs.category')"
          :placeholder="$t('new-event.basic-information.placeholders.category')"
          :items="$produtos_gerenciar.categorias_produtos"
          item-text="descricao"
          item-value="idcategoria"
          :loading="getLoadingCategories"
          class="rounded-lg"
          dense
          required
          persistent-placeholder
          outlined
          :rules="[rules.required]"
          v-model="getEventBasicData.category"
        />
      </v-col>
      <v-col sm="4" cols="12">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="ml-sm-4 mb-2 rounded-pill bg-custom-grey"
              outlined
              :color="$config.dados.primary"
              dark
              v-bind="attrs"
              v-on="on"
            >
              {{ $t("new-event.basic-information.add-locale") }}
            </v-btn>
          </template>
          <v-list v-if="localesWithNames.length > 0">
            <v-list-item
              v-for="(locale, index) in localesWithNames"
              :key="index"
              @click="addLocale(locale.code)"
            >
              <v-list-item-title>{{ locale.name }}</v-list-item-title>
            </v-list-item>
          </v-list>
          <v-list v-else>
            <v-list-item>
              <v-list-item-title>{{
                $t("new-event.basic-information.no-locales")
              }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Data do Evento -->
    <TopicTitle
      :title="$t('new-event.basic-information.event-date')"
      icon="mdi-calendar-blank-outline"
      v-if="getEventType !== 'LINK'"
    />
    <v-row no-gutters v-if="getEventType !== 'LINK'">
      <v-col sm="4" cols="12">
        <v-radio-group height="1" class="mb-4" row v-model="selectedFrequency">
          <v-radio
            class="mr-5"
            :label="$t('new-event.basic-information.inputs.event-unique')"
            value="UNICO"
          />
          <v-radio
            :label="$t('new-event.basic-information.inputs.event-recurrent')"
            value="RECORRENTE"
          >
            <template v-slot:label>
              <div>
                {{ $t("new-event.basic-information.inputs.event-recurrent") }}
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      class="pb-3"
                      v-bind="attrs"
                      v-on="on"
                      color="primary"
                    >
                      mdi-information
                    </v-icon>
                  </template>
                  <span>{{
                    $t("new-event.basic-information.event-dates-description")
                  }}</span>
                </v-tooltip>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
        <ConfirmAction
          :dialog.sync="showConfirmation"
          :title="$t('new-event.recurring-event.change-config-title')"
          :subtitle="$t('new-event.recurring-event.change-config-subtitle')"
          @action="confirmChange"
          @close="cancelChange"
        />

        <date-time-picker
          v-if="!getIsRecurringEvent"
          :rules="[rules.required]"
          :label="$t('new-event.basic-information.inputs.event-date')"
          @formatted-date="eventDateFormattedDate"
          :edit="getEventBasicData.eventDate"
          :allowed-dates="allowedEventDate"
        ></date-time-picker>
      </v-col>
    </v-row>

    <!-- Período de vendas -->
    <v-col class="ma-0 pa-0 mb-4" md="8" cols="12">
      <TopicTitle
        :title="$t('new-event.basic-information.sales-periods')"
        icon="mdi-calendar"
        :subtitle="
          getIsRecurringEvent
            ? $t(
                'new-event.basic-information.sales-periods-description-recurring'
              )
            : $t('new-event.basic-information.sales-periods-description')
        "
        v-if="getEventType !== 'LINK'"
        class="mt-6"
      />
    </v-col>
    <v-row no-gutters v-if="getEventType !== 'LINK'">
      <v-col class="mr-3 mb-4" sm="4" cols="12">
        <date-time-picker
          :rules="[rules.required]"
          :label="$t('new-event.basic-information.inputs.sale-date-start')"
          @formatted-date="saleStartFormattedDate"
          :edit="getEventBasicData.saleStart"
          :allowed-dates="
            getIsRecurringEvent ? null : allowedPeriodStartSaleDates
          "
        ></date-time-picker>
      </v-col>
      <v-col sm="4" cols="12" v-if="!getIsRecurringEvent">
        <date-time-picker
          :rules="[rules.required]"
          :label="$t('new-event.basic-information.inputs.sale-date-end')"
          @formatted-date="saleEndFormattedDate"
          :edit="getEventBasicData.saleEnd"
          :allowed-dates="allowedPeriodEndSaleDates"
        ></date-time-picker>
      </v-col>
      <v-col sm="4" cols="12" v-else>
        <BaseTextField
          :label="$t('new-event.basic-information.inputs.sale-hour-end')"
          placeholder="0"
          v-model="getEventBasicData.salesEndHoursAfter"
          autocomplete="new"
          type="number"
          :rules="[rules.required]"
          persistent-hint
          :hint="$t('new-event.basic-information.inputs.sale-hour-end-hint')"
        />
      </v-col>
    </v-row>

    <!-- Endereço do Evento -->
    <TopicTitle
      :title="$t('new-event.basic-information.event-address')"
      icon="mdi-map-marker-radius-outline"
    />
    <v-row class="mt-4" no-gutters>
      <v-col class="mr-3" sm="4" cols="12">
        <BaseTextField
          required
          :label="$t('new-event.basic-information.inputs.location')"
          :placeholder="$t('new-event.basic-information.placeholders.location')"
          autocomplete="new"
          class="mb-4"
          :rules="[rules.required]"
          v-model="getEventBasicData.location"
          maxlength="65"
        />
      </v-col>
      <v-col sm="4" cols="12">
        <v-autocomplete
          :label="$t('new-event.basic-information.inputs.country')"
          :placeholder="$t('new-event.basic-information.placeholders.country')"
          class="rounded-lg mb-4"
          required
          dense
          outlined
          persistent-placeholder
          :items="countries?.result"
          item-text="dsc"
          item-value="id"
          return-object
          :rules="[rules.required]"
          v-model="addresses.country"
          hide-details="auto"
        />
      </v-col>
      <v-col class="mr-3" sm="4" cols="12">
        <v-autocomplete
          :label="$t('new-event.basic-information.inputs.state')"
          :placeholder="$t('new-event.basic-information.placeholders.state')"
          class="rounded-lg mb-4"
          required
          dense
          outlined
          persistent-placeholder
          :items="states?.result"
          item-text="dsc"
          item-value="id"
          return-object
          autocomplete="new"
          :rules="[rules.required]"
          v-model="addresses.state"
          :menu-props="{ top: getEventType === 'LINK' }"
          hide-details="auto"
        />
      </v-col>
      <v-col sm="4" cols="12">
        <v-autocomplete
          :label="$t('new-event.basic-information.inputs.city')"
          :placeholder="$t('new-event.basic-information.placeholders.city')"
          class="rounded-lg mb-4"
          dense
          outlined
          required
          persistent-placeholder
          :items="cities?.result"
          item-text="dsc"
          item-value="id"
          return-object
          autocomplete="new"
          :rules="[rules.required]"
          v-model="addresses.city"
          :menu-props="{ top: getEventType === 'LINK' }"
          hide-details="auto"
        />
      </v-col>
      <v-col class="mr-3" sm="4" cols="12">
        <BaseTextField
          :label="$t('new-event.basic-information.inputs.latitude')"
          :placeholder="$t('new-event.basic-information.placeholders.latitude')"
          autocomplete="new"
          class="mb-sm-0 mb-4"
          v-model="getEventBasicData.latitude"
          :rules="
            getEventBasicData.longitude
              ? [rules.latitude, rules.required]
              : [rules.latitude]
          "
        />
      </v-col>
      <v-col class="mr-3" sm="4" cols="12">
        <BaseTextField
          :label="$t('new-event.basic-information.inputs.longitude')"
          :placeholder="
            $t('new-event.basic-information.placeholders.longitude')
          "
          autocomplete="new"
          v-model="getEventBasicData.longitude"
          :rules="
            getEventBasicData.latitude
              ? [rules.longitude, rules.required]
              : [rules.longitude]
          "
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-checkbox
          :label="$t('new-event.basic-information.inputs.map')"
          v-model="getEventBasicData.showMap"
          v-if="isAdmin && getEventType === 'EVENTO'"
          dense
        />
      </v-col>
    </v-row>

    <!-- Descrição do Evento -->
    <TopicTitle
      :title="$t('new-event.basic-information.event-description')"
      :subtitle="
        $t('new-event.basic-information.event-description-description')
      "
      icon="mdi-text-box-outline"
      v-if="getEventType === 'EVENTO'"
    />
    <v-row no-gutters v-if="getEventType === 'EVENTO'">
      <v-switch
        :label="$t('new-event.basic-information.inputs.expand-description')"
        v-model="getEventBasicData.expandDescription"
        v-if="getEventType === 'EVENTO'"
        color="primary"
      />
      <v-col cols="12">
        <small class="primary--text">{{
          `${selectedLocales?.length > 0 ? `${getTextLocale(locale)}` : ""}`
        }}</small>
        <RichText
          :form="$refs?.form"
          :id="`richtext-${locale}`"
          :rules="[rules.required]"
          v-model="descriptionDefault"
          @update:content="descriptionContent = $event"
        />
      </v-col>
      <v-col
        class="mt-4"
        v-for="(locale, index) in selectedLocales"
        :key="locale"
        cols="12"
      >
        <small class="primary--text">{{ getTextLocale(locale) }}</small>
        <RichText
          :form="$refs?.form"
          :id="`richtext-${index}`"
          :rules="[rules.required]"
          v-model="descriptionLocales[locale]"
          @update:content="descriptionContent = $event"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import TopicTitle from "../TopicTitle.vue";
import BaseTextField from "../forms/BaseTextField.vue";
import DateTimePicker from "../forms/DateTimePicker.vue";
import RichText from "@/components/RichText.vue";
import ConfirmAction from "./EventSettingsModal/ConfirmAction.vue";
import { getTextLocale } from "@/utils";
import { mapGetters, mapActions, mapMutations } from "vuex";
import mixins from "@/mixins";
import { processEventName } from "../../utils";
const { formRules } = mixins;

export default {
  name: "BasicInformation",
  components: {
    TopicTitle,
    BaseTextField,
    DateTimePicker,
    RichText,
    ConfirmAction,
  },
  mixins: [formRules],
  data() {
    return {
      getTextLocale,
      form: null,
      nameDefault: "",
      nameLocales: {},
      descriptionDefault: "",
      descriptionLocales: {},
      selectedFrequency: "",
      previousFrequency: "",
      showConfirmation: false,
    };
  },
  computed: {
    ...mapGetters([
      "getEventBasicData",
      "getSelectedLocales",
      "getLoadingCategories",
      "categories",
      "countries",
      "states",
      "cities",
      "addresses",
      "getCurrentEvent",
      "isAdmin",
      "getEventType",
      "getIsRecurringEvent",
      "getTicketList",
    ]),
    selectedLocales() {
      return this.getSelectedLocales;
    },
    formattedEventNames() {
      const defaultName = `<${this.locale}>${this.nameDefault}</${this.locale}>`;
      if (this.selectedLocales.length === 0) {
        return defaultName;
      }
      return (
        defaultName +
        this.selectedLocales
          .map(
            selectedLocale =>
              `<${selectedLocale}>${this.nameLocales[selectedLocale]}</${selectedLocale}>`
          )
          .join("")
      );
    },
    formattedEventDescriptions() {
      const defaultDescription = `<${this.locale}>${this.descriptionDefault}</${this.locale}>`;
      if (this.selectedLocales.length === 0) {
        return defaultDescription;
      }
      return (
        defaultDescription +
        this.selectedLocales
          .map(
            selectedLocale =>
              `<${selectedLocale}>${this.descriptionLocales[selectedLocale]}</${selectedLocale}>`
          )
          .join("")
      );
    },
    localesWithNames() {
      return this.$i18n.availableLocales
        ?.filter(
          loc => loc !== this.locale && !this.selectedLocales.includes(loc)
        )
        .map(loc => {
          return {
            code: loc,
            name: getTextLocale(loc),
          };
        });
    },
    locale() {
      return this.$i18n.locale;
    },
  },
  methods: {
    ...mapActions([
      "getCountries",
      "watchCountry",
      "watchState",
      "populateLocation",
      "canChangeEventType",
      "showSnackbarError",
      "removeTicket",
    ]),

    ...mapMutations([
      "setSelectedLocales",
      "setEventBasicData",
      "setDatesOfTickets",
    ]),
    allowedEventDate(date) {
      const selectedDate = new Date(date);
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      return selectedDate >= yesterday;
    },
    allowedPeriodStartSaleDates(date) {
      const selectedDate = new Date(date);
      const eventDate = new Date(this.getEventBasicData.eventDate);
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      return selectedDate >= yesterday && selectedDate <= eventDate;
    },
    allowedPeriodEndSaleDates(date) {
      const selectedDate = new Date(date);
      const startSaleDate = new Date(this.getEventBasicData.saleStart);
      return selectedDate >= startSaleDate;
    },
    addLocale(loc) {
      if (!this.selectedLocales.includes(loc)) {
        this.selectedLocales.push(loc);
      } else {
        this.getSelectedLocales = this.selectedLocales.filter(
          selectedLoc => selectedLoc !== loc
        );
      }
    },
    eventDateFormattedDate(formatteddatetime) {
      this.getEventBasicData.eventDate = formatteddatetime;
    },
    saleStartFormattedDate(formatteddatetime) {
      this.getEventBasicData.saleStart = formatteddatetime;
    },
    saleEndFormattedDate(formatteddatetime) {
      this.getEventBasicData.saleEnd = formatteddatetime;
    },
    clearNameLocale(locale) {
      this.$delete(this.nameLocales, locale);
      this.setSelectedLocales(
        this.selectedLocales.filter(loc => loc !== locale)
      );
    },
    confirmChange() {
      this.getEventBasicData.frequency = "UNICO";
      this.previousFrequency = "UNICO";
      this.showConfirmation = false;
      this.setDatesOfTickets([]);
      this.getTicketList.forEach(ticket => {
        this.removeTicket(ticket);
      });
    },
    cancelChange() {
      this.selectedFrequency = "RECORRENTE";
      this.showConfirmation = false;
    },
  },
  watch: {
    locale() {
      this.setSelectedLocales([]);
    },
    formattedEventNames() {
      this.getEventBasicData.name = this.formattedEventNames;
    },
    formattedEventDescriptions() {
      this.getEventBasicData.description = this.formattedEventDescriptions;
    },
    "addresses.country"() {
      this.watchCountry();
    },
    "addresses.state"() {
      this.watchState();
    },
    async selectedFrequency(newVal) {
      if (this.previousFrequency === "RECORRENTE" && newVal === "UNICO") {
        try {
          await this.canChangeEventType();
          this.showConfirmation = true;
          this.getEventBasicData.frequency = newVal;
        } catch (error) {
          this.previousFrequency = "RECORRENTE";
          this.getEventBasicData.frequency = "RECORRENTE";
          this.selectedFrequency = "RECORRENTE";
          this.showSnackbarError(
            this.$t("new-event.recurring-event.change-config-error")
          );
        }
      } else {
        this.previousFrequency = newVal;
        this.getEventBasicData.frequency = newVal;
      }
    },
  },
  beforeMount() {
    if (this.getEventBasicData?.frequency) {
      this.selectedFrequency = this.getEventBasicData.frequency;
      this.previousFrequency = this.getEventBasicData.frequency;
    }
    if (this.$route.path.includes("cadastrar")) {
      this.getCountries();
    }
    if (this.getEventBasicData?.name) {
      const names = processEventName(this.getEventBasicData.name);
      this.nameDefault = names[this.locale] || "";
      this.nameLocales = names;
    }
    if (this.getEventBasicData?.description) {
      const descriptions = processEventName(this.getEventBasicData.description);
      this.descriptionDefault = descriptions[this.locale] || "";
      this.descriptionLocales = descriptions;
    }
  },
};
</script>

<style scoped></style>
